import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { FiCheck, FiChevronRight, FiX, FiZap } from "react-icons/fi";
import { tokens } from "../../screens/activity-logs/styles";
import { Capsule, PrimaryCapsule, RoundButton } from "../../screens/creditos/components/ControlBar";
import { SANS } from "../../screens/creditos/styles";
import { tokens as T, hairline } from "../../styles/designTokens";

/**
 * Destaque e cartão do tutorial. O destaque acompanha o elemento real a cada
 * quadro, então funciona com rolagem, gráficos carregando e mudança de tamanho.
 * Fora do destaque, os cliques ficam bloqueados; no passo de ação, o elemento
 * destacado continua clicável.
 */

const Z = 12000;
const PAD = 8;
const FIND_TIMEOUT = 2500;
const TOP_OFFSET = 190;

const rise = keyframes`from { opacity: 0; transform: translateY(8px) scale(0.985); } to { opacity: 1; transform: none; }`;
const pulse = keyframes`0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.55); } 70% { box-shadow: 0 0 0 10px rgba(16,185,129,0); } 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }`;

const Hole = styled.div`
  position: fixed;
  z-index: ${Z};
  border-radius: 14px;
  pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, ${({ theme }) => (tokens.dk(theme) ? 0.62 : 0.42)});
  transition: top 320ms ${T.easeOut}, left 320ms ${T.easeOut}, width 320ms ${T.easeOut}, height 320ms ${T.easeOut};
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Ring = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 2px solid ${T.brand};
  animation: ${pulse} 1.6s ease-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Blocker = styled.div`
  position: fixed;
  z-index: ${Z};
`;

const Card = styled.div`
  position: fixed;
  z-index: ${Z + 1};
  width: min(360px, calc(100vw - 24px));
  padding: 16px 18px 14px;
  border-radius: 18px;
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => tokens.label(theme)};
  background: ${({ theme }) => tokens.solid(theme)};
  border: 0.5px solid ${({ theme }) => hairline(theme, true)};
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
  animation: ${rise} 240ms ${T.easeOut} both;
  transition: top 320ms ${T.easeOut}, left 320ms ${T.easeOut};
  @media (prefers-reduced-motion: reduce) { animation: none; transition: none; }

  .top { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .chap { font-size: 0.74em; font-weight: 500; color: ${({ theme }) => tokens.label2(theme)}; }
  .bar { display: flex; gap: 3px; margin-top: 8px; }
  .bar i { flex: 1; height: 3px; border-radius: 2px; background: rgba(118,118,128,0.22); transition: background-color 240ms ease; }
  .bar i.on { background: ${T.brand}; }
  h4 { margin: 12px 0 0; font-size: 1.04em; font-weight: 600; letter-spacing: -0.02em; line-height: 1.25; }
  p { margin: 6px 0 0; font-size: 0.86em; line-height: 1.5; color: ${({ theme }) => tokens.label2(theme)}; }
  .insight { display: flex; gap: 8px; margin-top: 10px; padding: 9px 11px; border-radius: 11px; font-size: 0.82em; line-height: 1.42; background: ${T.brandSoft}; }
  .insight svg { flex-shrink: 0; margin-top: 2px; color: ${T.brand}; }
  .hint { display: flex; align-items: center; gap: 7px; margin-top: 10px; font-size: 0.82em; font-weight: 600; color: ${T.brand}; }
  .actions { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 14px; }
  .actions > div { display: flex; gap: 6px; margin-left: auto; }
  .count { font-size: 0.74em; color: ${({ theme }) => tokens.label3(theme)}; }
  .list { margin-top: 12px; display: flex; flex-direction: column; gap: 4px; }
  .next { display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 10px; border: none; border-radius: 11px; background: rgba(118,118,128,0.1); color: inherit; font: inherit; text-align: left; cursor: pointer; transition: background-color 160ms ease; }
  .next:hover { background: rgba(118,118,128,0.18); }
  .next:focus-visible { outline: 2px solid ${T.brand}; outline-offset: -2px; }
  .next b { display: block; font-size: 0.86em; font-weight: 600; }
  .next span { font-size: 0.76em; color: ${({ theme }) => tokens.label2(theme)}; }
  .next svg { margin-left: auto; flex-shrink: 0; color: ${({ theme }) => tokens.label3(theme)}; }
  .okay { display: flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 50%; font-size: 19px; color: ${T.brand}; background: ${T.brandSoft}; }
`;

const asList = (target) => (Array.isArray(target) ? target : [target]).filter(Boolean);

function measure(target) {
  const nodes = asList(target).flatMap((sel) => [...document.querySelectorAll(sel)]);
  const rects = nodes.map((n) => n.getBoundingClientRect()).filter((r) => r.width > 0 && r.height > 0);
  if (rects.length === 0) return { rect: null, first: null };
  const top = Math.min(...rects.map((r) => r.top));
  const left = Math.min(...rects.map((r) => r.left));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return { rect: { top, left, width: right - left, height: bottom - top, bottom, right }, first: nodes[0] };
}

// Acompanha o elemento e avisa se ele não aparecer
function useTargetRect(step, onMissing) {
  const [rect, setRect] = useState(null);
  const missingRef = useRef(onMissing);
  missingRef.current = onMissing;

  useEffect(() => {
    if (!step?.target) { setRect(null); return undefined; }
    setRect(null);
    let frame;
    let scrolled = false;
    let last = "";
    const startedAt = performance.now();
    const tick = () => {
      const { rect: r, first } = measure(step.target);
      if (r) {
        if (!scrolled) {
          scrolled = true;
          const vh = window.innerHeight;
          const outside = r.top < TOP_OFFSET || r.bottom > vh - 24;
          if ((outside || step.alwaysScroll) && !step.noScroll && first) {
            const tall = r.height > vh - TOP_OFFSET - 40;
            first.style.scrollMarginTop = `${TOP_OFFSET}px`;
            first.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: tall ? "start" : "center" });
          }
        }
        const key = `${Math.round(r.top)}|${Math.round(r.left)}|${Math.round(r.width)}|${Math.round(r.height)}`;
        if (key !== last) { last = key; setRect(r); }
      } else if (performance.now() - startedAt > FIND_TIMEOUT) {
        missingRef.current();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.id]);

  return rect;
}

function placeCard(rect, card, preferred) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 14;
  const margin = 12;
  if (!rect) return { top: Math.max(margin, (vh - card.height) / 2), left: Math.max(margin, (vw - card.width) / 2) };
  const hole = { top: rect.top - PAD, left: rect.left - PAD, bottom: rect.bottom + PAD, right: rect.right + PAD };
  const clampX = (x) => Math.min(Math.max(margin, x), vw - card.width - margin);
  const clampY = (y) => Math.min(Math.max(margin, y), vh - card.height - margin);
  const centerX = clampX(rect.left + rect.width / 2 - card.width / 2);
  const centerY = clampY(rect.top + rect.height / 2 - card.height / 2);
  const options = {
    bottom: { fits: hole.bottom + gap + card.height <= vh - margin, top: hole.bottom + gap, left: centerX },
    top: { fits: hole.top - gap - card.height >= margin, top: hole.top - gap - card.height, left: centerX },
    right: { fits: hole.right + gap + card.width <= vw - margin, top: centerY, left: hole.right + gap },
    left: { fits: hole.left - gap - card.width >= margin, top: centerY, left: hole.left - gap - card.width },
  };
  const order = [preferred, "bottom", "top", "right", "left"].filter(Boolean);
  const pick = order.find((o) => options[o].fits);
  // Elemento muito grande: o cartão fica no canto inferior direito, sobre ele
  if (!pick) return { top: vh - card.height - margin, left: vw - card.width - margin };
  return { top: options[pick].top, left: options[pick].left };
}

export default function TourOverlay({ tour }) {
  const {
    active, chapter, step, steps, index, ended, ctx, next, back, close, perform, actionDone, skipMissing, start, pendingChapters, notice,
  } = tour;
  const rect = useTargetRect(step, skipMissing);
  const cardRef = useRef(null);
  const primaryRef = useRef(null);
  const [cardSize, setCardSize] = useState({ width: 360, height: 240 });
  const isOpen = !!active && (ended || !!step);
  const waitingTarget = !!step?.target && !rect;

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const { offsetWidth, offsetHeight } = cardRef.current;
    if (offsetWidth !== cardSize.width || offsetHeight !== cardSize.height) setCardSize({ width: offsetWidth, height: offsetHeight });
  }, [cardSize.width, cardSize.height, step?.id, ended, rect]);

  // O foco acompanha o passo, para funcionar pelo teclado
  useEffect(() => {
    if (isOpen && !waitingTarget) primaryRef.current?.focus({ preventScroll: true });
  }, [isOpen, waitingTarget, step?.id, ended]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(); }
      if (e.key === "ArrowRight" && !step?.action && !ended) { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft" && !ended) { e.preventDefault(); back(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, step, ended, next, back, close]);

  if (!isOpen) return null;

  const hole = rect ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 } : null;
  const pos = placeCard(rect, cardSize, step?.placement);
  const body = typeof step?.body === "function" ? step.body(ctx) : step?.body;
  const insight = step?.insight ? step.insight(ctx) : null;
  const isLast = index >= steps.length - 1;
  const remaining = pendingChapters.filter((c) => c.key !== chapter?.key);

  // Bloqueia cliques fora do destaque; no passo de ação, o destaque fica livre
  const blockers = hole ? [
    { top: 0, left: 0, width: "100vw", height: Math.max(0, hole.top) },
    { top: hole.top + hole.height, left: 0, width: "100vw", bottom: 0 },
    { top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height },
    { top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height },
    ...(step?.action ? [] : [{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }]),
  ] : [{ top: 0, left: 0, right: 0, bottom: 0 }];

  return createPortal(
    <>
      {blockers.map((b, i) => <Blocker key={i} style={b} aria-hidden="true" />)}
      {hole
        ? <Hole style={hole}>{step?.action && !actionDone && <Ring />}</Hole>
        : <Hole style={{ top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0 }} />}

      {!waitingTarget && (
        <Card ref={cardRef} style={pos} role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-live="polite" key={ended ? "end" : step?.id}>
          {ended ? (
            <>
              <div className="top">
                <span className="okay"><FiCheck /></span>
                <RoundButton type="button" onClick={close} aria-label="Fechar tutorial" title="Fechar" style={{ width: 28, height: 28, fontSize: 13 }}><FiX /></RoundButton>
              </div>
              <h4 id="tour-title">{chapter.title}: concluído</h4>
              <p>{remaining.length > 0 ? "Quer conhecer mais? Você também pode voltar a qualquer momento pelo botão de ajuda." : notice || "Você viu tudo desta tela. O botão de ajuda continua disponível, com o glossário dos termos."}</p>
              {remaining.length > 0 && (
                <div className="list">
                  {remaining.map((c) => (
                    <button key={c.key} type="button" className="next" onClick={() => start(c.key)}>
                      <div><b>{c.title}</b><span>{c.summary}</span></div>
                      <FiChevronRight />
                    </button>
                  ))}
                </div>
              )}
              <div className="actions"><div><PrimaryCapsule ref={primaryRef} type="button" onClick={close}>Concluir</PrimaryCapsule></div></div>
            </>
          ) : (
            <>
              <div className="top">
                <span className="chap">{chapter.title}</span>
                <RoundButton type="button" onClick={close} aria-label="Sair do tutorial" title="Sair (Esc)" style={{ width: 28, height: 28, fontSize: 13 }}><FiX /></RoundButton>
              </div>
              <div className="bar" aria-hidden="true">{steps.map((s, i) => <i key={s.id} className={i <= index ? "on" : ""} />)}</div>
              <h4 id="tour-title">{step.title}</h4>
              <p>{body}</p>
              {insight && <div className="insight"><FiZap /><span>{insight}</span></div>}
              {step.action && !actionDone && <div className="hint">{step.action.hint}</div>}
              <div className="actions">
                <span className="count">{index + 1} de {steps.length}</span>
                <div>
                  {index > 0 && <Capsule type="button" onClick={back}>Voltar</Capsule>}
                  {step.action
                    ? <PrimaryCapsule ref={primaryRef} type="button" onClick={perform} disabled={actionDone}>{actionDone ? "Pronto" : "Fazer por mim"}</PrimaryCapsule>
                    : <PrimaryCapsule ref={primaryRef} type="button" onClick={next}>{isLast ? "Concluir" : "Próximo"}</PrimaryCapsule>}
                </div>
              </div>
            </>
          )}
        </Card>
      )}
    </>,
    document.body,
  );
}
